<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Payout Management' : 'Deleted Payouts'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all payouts' : 'Restore or permanently delete payouts'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      v-if="!isSeller"
                      color="primary"
                      elevation="1"
                      small
                      to="/payouts/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Payout
                    </v-btn>
                    <v-btn
                      color="success"
                      outlined
                      small
                      @click="exportExcel"
                      :loading="exporting"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      Export
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Payouts</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Payouts"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Transaction ID, user name..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Payout Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.payout_status"
                    :items="payoutStatuses"
                    label="Payout Status"
                    prepend-inner-icon="mdi-cash"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- User Filter — hidden for sellers -->
                <v-col cols="12" md="3" v-if="!isSeller">
                  <v-autocomplete
                    v-model="filter.user_id"
                    :items="users"
                    label="User"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Payment Method Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.payment_method"
                    :items="paymentMethods"
                    label="Payment Method"
                    prepend-inner-icon="mdi-credit-card"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Payouts Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Payouts List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-cash-multiple</v-icon>
                      {{ count }} {{ count === 1 ? "Payout" : "Payouts" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && payouts.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="exportExcel"
                      class="px-2"
                      :loading="exporting"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-4"></v-divider>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="loading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading payouts..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="payouts"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- ID Column -->
                <template #[`item.id`]="{ item }">
                  <v-chip
                    color="info"
                    x-small
                    outlined
                    class="font-weight-bold"
                  >
                    {{ item.id }}
                  </v-chip>
                </template>

                <!-- Generated Transaction ID Column -->
                <template #[`item.generated_transaction_id`]="{ item }">
                  <code class="text-caption font-weight-medium">
                    {{ item.generated_transaction_id || "N/A" }}
                  </code>
                </template>

                <!-- User Column -->
                <template v-slot:[`item.user`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="primary">
                      <span class="white--text text-caption">{{
                        getInitials(getUserFullName(item.user))
                      }}</span>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ getUserFullName(item.user) || "N/A" }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.user?.email || "N/A" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Created By Column -->
                <template v-slot:[`item.created_by`]="{ item }">
                  <span class="text-body-2 font-weight-medium">
                    {{ getUserFullName(item.created_by) || "N/A" }}
                  </span>
                </template>

                <!-- Amount Column -->
                <template v-slot:[`item.amount`]="{ item }">
                  <span class="text-body-2 font-weight-bold">
                    ₹{{ formatCurrency(item.amount) }}
                  </span>
                </template>

                <!-- Payout Status Column -->
                <template v-slot:[`item.payout_status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.payout_status)"
                    small
                    class="text-capitalize"
                  >
                    {{ getStatusText(item.payout_status) }}
                  </v-chip>
                </template>

                <!-- Payment Method Column -->
                <template v-slot:[`item.payment_method`]="{ item }">
                  <span class="text-body-2">{{
                    getPaymentMethodText(item.payment_method)
                  }}</span>
                </template>

                <!-- Payment Date Column -->
                <template v-slot:[`item.payment_date`]="{ item }">
                  <span class="text-body-2">{{
                    item.payment_date
                      ? formatDate(item.payment_date)
                      : "Not processed"
                  }}</span>
                </template>



                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button (hidden for sellers) -->
                      <v-tooltip bottom v-if="!isSeller">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/payouts/${item.id}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Payout</span>
                      </v-tooltip>

                      <!-- Download Payout Slip Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="info"
                            @click="downloadPayoutSlip(item)"
                            :loading="item.downloading"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-download</v-icon>
                          </v-btn>
                        </template>
                        <span>Download Payout Slip</span>
                      </v-tooltip>

                      <!-- Delete Button (hidden for sellers) -->
                      <v-tooltip bottom v-if="!isSeller">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Payout</span>
                      </v-tooltip>

                      <!-- Quick Actions for Pending Payouts (hidden for sellers) -->
                      <v-menu
                        v-if="item.payout_status === 0 && !isSeller"
                        offset-y
                        left
                      >
                        <template #activator="{ on, attrs }">
                          <v-tooltip bottom>
                            <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                              <v-btn
                                x-small
                                color="info"
                                v-bind="{ ...attrs, ...tooltipAttrs }"
                                v-on="{ ...on, ...tooltipOn }"
                              >
                                <v-icon x-small>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <span>Quick Actions</span>
                          </v-tooltip>
                        </template>
                        <v-list dense>
                          <v-list-item @click="markCompleted(item)">
                            <v-list-item-icon>
                              <v-icon small color="success">mdi-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Mark Completed</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item @click="markFailed(item)">
                            <v-list-item-icon>
                              <v-icon small color="error">mdi-close</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Mark Failed</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="restore(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Payout</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-cash-multiple</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Payouts Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No payouts match your current filters. Try adjusting your search criteria."
                          : "You haven't added any payouts yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage() && !isSeller"
                      color="primary"
                      to="/payouts/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Payout
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mark Completed Dialog -->
      <v-dialog v-model="markCompletedDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Mark Payout as Completed</v-card-title>
          <v-card-text>
            <v-form ref="completedForm" v-model="validCompleted">
              <v-text-field
                v-model="completedForm.transaction_id"
                label="Transaction ID"
                prepend-inner-icon="mdi-identifier"
                solo
                rounded
                dense
                placeholder="Enter transaction ID from payment gateway"
              ></v-text-field>
              <v-text-field
                v-model="completedForm.payment_date"
                label="Payment Date"
                type="datetime-local"
                prepend-inner-icon="mdi-calendar"
                solo
                rounded
                dense
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="markCompletedDialog = false">Cancel</v-btn>
            <v-btn
              color="success"
              @click="confirmMarkCompleted"
              :loading="processingCompleted"
            >
              Mark Completed
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Mark Failed Dialog -->
      <v-dialog v-model="markFailedDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Mark Payout as Failed</v-card-title>
          <v-card-text>
            <v-form ref="failedForm" v-model="validFailed">
              <v-textarea
                v-model="failedForm.reason"
                label="Failure Reason"
                prepend-inner-icon="mdi-alert"
                solo
                rounded
                dense
                rows="3"
                placeholder="Describe why the payout failed..."
              ></v-textarea>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="markFailedDialog = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="confirmMarkFailed"
              :loading="processingFailed"
            >
              Mark Failed
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this payout?
              </h3>

              <div
                class="payout-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-cash-multiple</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      Payout ID: {{ payoutToDelete ? payoutToDelete.id : "" }}
                    </div>
                    <div class="text-caption grey--text">
                      Amount: ₹{{ payoutToDelete ? formatCurrency(payoutToDelete.amount) : "0.00" }}
                    </div>
                    <div class="text-caption grey--text">
                      User: {{ payoutToDelete && payoutToDelete.user ? getUserFullName(payoutToDelete.user) : "N/A" }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the payout
                to the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted payouts section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Payout
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "PayoutsIndex",
  data() {
    return {
      loading: false,
      exporting: false,
      payouts: [],
      count: 0,
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      jumpToPage: 1,
      viewDeleted: 0,
      
      // Form dialogs
      markCompletedDialog: false,
      markFailedDialog: false,
      processingCompleted: false,
      processingFailed: false,
      validCompleted: false,
      validFailed: false,
      selectedPayout: null,
      
      // Delete confirmation
      showDeleteDialog: false,
      payoutToDelete: null,
      isDeleting: false,
      
      completedForm: {
        transaction_id: "",
        payment_date: "",
      },
      
      failedForm: {
        reason: "",
      },

      // Filters
      filter: {
        search_keyword: null,
        is_active: null,
        payout_status: null,
        user_id: null,
        payment_method: null,
      },

      // Dropdown options
      users: [],
      paymentMethods: [],
      payoutStatuses: [],

      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],

      rowsPerPageItems: [10, 15, 25, 50, 100],

      allHeaders: [
        { text: "#",               value: "sr_no",                    sortable: false, align: "center", width: "55px" },
        { text: "Unique ID",       value: "id",                       sortable: true,  align: "center", width: "90px" },
        { text: "Transaction ID",  value: "generated_transaction_id", sortable: false, align: "left",  width: "220px" },
        { text: "User",            value: "user",                     sortable: false, align: "left",  width: "200px" },
        { text: "Created By",      value: "created_by",               sortable: false, align: "left",  width: "150px" },
        { text: "Amount",          value: "amount",                   sortable: true,  align: "right",  width: "110px" },
        { text: "Status",          value: "payout_status",            sortable: true,  align: "center", width: "120px" },
        { text: "Payment Method",  value: "payment_method",           sortable: false, align: "left",   width: "140px" },
        { text: "Payment Date",    value: "payment_date",             sortable: true,  align: "left",   width: "160px" },
        { text: "Actions",         value: "actions",                  sortable: false, align: "center", width: "100px" },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return Object.values(this.filter).some((value) => value !== null && value !== "");
    },
    isSeller() {
      return this.user && this.user.position && this.user.position.name
        ? this.user.position.name.toUpperCase() === 'SELLER'
        : false;
    },
    headers() {
      if (!this.isSeller) return this.allHeaders;
      const hidden = ['id', 'user', 'is_active'];
      return this.allHeaders.filter(h => !hidden.includes(h.value));
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.viewDeleted = this.$route.name === 'payouts-deleted' ? 1 : 0;
    this.getData();
    this.getDropdownData();
  },

  methods: {
    async getData() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          show_deleted: this.viewDeleted,
          ...this.filter,
        };

        const response = await axios.get("/payouts", { params });
        this.payouts = response.data.data;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.loading = false;
      }
    },

    async getDropdownData() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "payouts" }
        });
        
        this.users = response.data.users.map(user => ({
          text: `${user.first_name} ${user.last_name} (${user.email})`,
          value: user.id,
        }));
        
        this.paymentMethods = (response.data.payment_methods || [])
          .filter(m => m.description.toLowerCase() !== 'wallet')
          .map((method, index) => ({
            text: method.description,
            value: index + 1,
          }));

        const statusMapping = {
          'Pending': 0, 'Processing': 0, 'On Hold': 0, 'Under Review': 0,
          'Completed': 1, 'Approved': 1,
          'Failed': 2, 'Cancelled': 2, 'Rejected': 2,
        };
        this.payoutStatuses = (response.data.payout_statuses || [])
          .filter(s => statusMapping[s.description] !== undefined)
          .map(s => ({
            text: s.description,
            value: statusMapping[s.description],
          }))
          .filter((s, i, arr) => arr.findIndex(x => x.value === s.value) === i);
      } catch (error) {
        console.error("Failed to load dropdown data:", error);
      }
    },

    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          show_deleted: this.viewDeleted,
          ...this.filter,
        };

        const response = await axios.get("/payouts/excel", {
          params,
          responseType: "blob",
        });

        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `payouts_${new Date().toISOString().split("T")[0]}.xlsx`;
        link.click();
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.exporting = false;
      }
    },

    showDeleteConfirmation(payout) {
      this.payoutToDelete = payout;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.payoutToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.payoutToDelete) return;

      this.isDeleting = true;

      try {
        await axios.post(`/payouts/delete/${this.payoutToDelete.id}`);
        this.showDeleteDialog = false;
        this.payoutToDelete = null;
        this.isDeleting = false;
        this.$store.dispatch("errors/showSuccess", "Payout deleted successfully");
        this.getData();
      } catch (error) {
        console.log(error);
        this.isDeleting = false;
        this.$store.dispatch("errors/handleError", error);
      }
    },

    async deletePayout(item) {
      if (
        confirm(
          `Are you sure you want to delete payout ${item.generated_transaction_id}?`
        )
      ) {
        try {
          await axios.post(`/payouts/delete/${item.id}`);
          this.$store.dispatch("errors/showSuccess", "Payout deleted successfully");
          this.getData();
        } catch (error) {
          this.$store.dispatch("errors/handleError", error);
        }
      }
    },

    async restore(item) {
      try {
        await axios.post(`/payouts/restore/${item.id}`);
        this.$store.dispatch("errors/showSuccess", "Payout restored successfully");
        this.getData();
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      }
    },

    markCompleted(item) {
      this.selectedPayout = item;
      this.completedForm = {
        transaction_id: "",
        payment_date: new Date().toISOString().slice(0, 16),
      };
      this.markCompletedDialog = true;
    },

    async confirmMarkCompleted() {
      this.processingCompleted = true;
      try {
        await axios.post(`/payouts/${this.selectedPayout.id}/mark-completed`, this.completedForm);
        this.$store.dispatch("errors/showSuccess", "Payout marked as completed");
        this.markCompletedDialog = false;
        this.getData();
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.processingCompleted = false;
      }
    },

    markFailed(item) {
      this.selectedPayout = item;
      this.failedForm = { reason: "" };
      this.markFailedDialog = true;
    },

    async confirmMarkFailed() {
      this.processingFailed = true;
      try {
        await axios.post(`/payouts/${this.selectedPayout.id}/mark-failed`, this.failedForm);
        this.$store.dispatch("errors/showSuccess", "Payout marked as failed");
        this.markFailedDialog = false;
        this.getData();
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.processingFailed = false;
      }
    },

    clearAllFilters() {
      this.filter = {
        search_keyword: null,
        is_active: null,
        payout_status: null,
        user_id: null,
        payment_method: null,
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      return Object.values(this.filter).filter((value) => value !== null && value !== "").length;
    },

    isDeletePage() {
      return this.viewDeleted === 1;
    },

    getInitials(name) {
      if (!name) return "N/A";
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    getUserFullName(user) {
      if (!user) return null;
      if (user.first_name || user.last_name) {
        return `${user.first_name || ""} ${user.last_name || ""}`.trim();
      }
      return user.name || null;
    },

    getStatusColor(status) {
      const colors = {
        0: "warning", // Pending
        1: "success", // Completed
        2: "error",   // Failed
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        0: "Pending",
        1: "Completed",
        2: "Failed",
      };
      return texts[status] || "Unknown";
    },

    getPaymentMethodText(method) {
      const methods = {
        1: "Bank Transfer",
        2: "UPI",
        3: "Wallet",
        4: "Cash",
        5: "Cheque",
      };
      return methods[method] || "Unknown";
    },

    formatCurrency(amount) {
      if (!amount) return "0.00";
      return parseFloat(amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    // Download payout slip as PDF
    async downloadPayoutSlip(payout) {
      try {
        // Set loading state
        this.$set(payout, 'downloading', true);

        // Make API call to download the payout slip
        const response = await axios.get(
          `/payouts/${payout.id}/download-slip`,
          {
            responseType: 'blob', // Important for file downloads
          }
        );

        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename based on payout data
        const recipientName = payout.user_name || `${payout.first_name || ''}_${payout.last_name || ''}`.trim() || 'recipient';
        const transactionId = payout.transaction_id || payout.generated_transaction_id || payout.id;
        const cleanName = recipientName.replace(/[^A-Za-z0-9_-]/g, '_');
        link.download = `payout_slip_${cleanName}_${transactionId}.pdf`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show success message
        this.$toast.success('Payout slip downloaded successfully!');
      } catch (error) {
        console.error('Error downloading payout slip:', error);
        
        // Show appropriate error message
        if (error.response && error.response.status === 404) {
          this.$toast.error('Payout not found');
        } else if (error.response && error.response.status === 403) {
          this.$toast.error('You do not have permission to download this payout slip');
        } else {
          this.$toast.error('Failed to download payout slip. Please try again.');
        }
      } finally {
        // Remove loading state
        this.$set(payout, 'downloading', false);
      }
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    viewDeleted() {
      this.page = 1;
      this.getData();
    },
    $route(to) {
      this.viewDeleted = to.name === 'payouts-deleted' ? 1 : 0;
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Delete Confirmation Dialog Styling */
.payout-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.payout-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>